import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { useTheme } from '../../context/ThemeContext';
import { Search, Filter, Download } from 'lucide-react';

const UserManagement = () => {
  const { t } = useTranslation();
  const { users, updateUserStatus } = useAdmin();
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: t('admin.activeUsers') },
      suspended: { color: 'bg-red-100 text-red-800', label: t('admin.suspended') },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: t('admin.pending') }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{t('admin.userManagement')}</h1>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('admin.manageAllUsers')} {t('common.of')} {t('common.your')} {t('common.business')}
          </p>
        </div>
      </motion.div>

      {/* Filtros y Búsqueda */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} w-5 h-5`} />
              <input
                type="text"
                placeholder={t('common.search') + ' ' + t('common.users').toLowerCase() + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} ${isDarkMode ? 'bg-gray-700' : 'bg-white'} ${isDarkMode ? 'text-white' : 'text-gray-900'} rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} ${isDarkMode ? 'bg-gray-700' : 'bg-white'} ${isDarkMode ? 'text-white' : 'text-gray-900'} rounded-2xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
          >
            <option value="all">{t('admin.allStatuses')}</option>
            <option value="active">{t('admin.activeUsers')}</option>
            <option value="suspended">{t('admin.suspended')}</option>
            <option value="pending">{t('admin.pending')}</option>
          </select>

          <button className={`inline-flex items-center px-6 py-3 border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} rounded-2xl text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}>
            <Download className="w-4 h-4 mr-2" />
            {t('admin.export')}
          </button>
        </div>
      </motion.div>

      {/* Grid de Usuarios */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {filteredUsers.slice(0, 12).map((user, index) => (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all duration-300`}
          >
            <div className="flex items-center space-x-4 mb-4">
              <img
                src={user.avatar}
                alt={user.name}
                className="w-12 h-12 rounded-2xl object-cover"
              />
              <div className="flex-1 min-w-0">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} truncate`}>
                  {user.name}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} truncate`}>{user.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('admin.rank')}:</span>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{user.rank}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('commissions.title')}:</span>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  ${user.totalCommission.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('admin.team')}:</span>
                <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  {user.teamSize} {t('admin.members')}
                </span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {getStatusBadge(user.status)}
              
              <button className={`text-orange-600 hover:text-orange-800 text-sm font-medium`}>
                {t('admin.viewDetails')}
              </button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default UserManagement;